import { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { openAccordion } from 'ducks/accordions'
import { resetSelection } from 'ducks/editor/selection'

import ColorControl from './Libraries/ColorControl'
import MenuControl from './Libraries/MenuControl'
import Slider from './Slider'
import TextControl from './TextControl'
import GenericInspectRow from './GenericRow'

import './BackgroundGradientControl.css'

type BackgroundGradient = {
  enabled: boolean
  type: 'linear' | 'radial'
  startColor: string
  endColor: string
  angle: number
}

type BackgroundGradientControlProps = {
  object: {
    backgroundColor?: string
    backgroundGradient?: BackgroundGradient
  }
  onChange: (values: Record<string, unknown>) => void
  isGradientDisabled?: boolean
  appId?: string
}

const backgroundModeOptions = [
  { label: 'Solid', value: 'solid' },
  { label: 'Gradient', value: 'gradient' },
]

const gradientTypeOptions = [
  { label: 'Linear', value: 'linear' },
  { label: 'Radial', value: 'radial' },
]

const DEFAULT_GRADIENT: BackgroundGradient = {
  enabled: true,
  type: 'linear',
  startColor: '@background',
  endColor: '@primary',
  angle: 180,
}

export const BackgroundGradientControl: React.FC<
  BackgroundGradientControlProps
> = ({ object, onChange, isGradientDisabled = false, appId }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const gradient = object.backgroundGradient
  const isGradientEnabled = useMemo(
    () => gradient?.enabled === true,
    [gradient]
  )

  const [wantsGradient, setWantsGradient] = useState(false)

  const showGradientHint =
    isGradientDisabled && (isGradientEnabled || wantsGradient)
  const currentMode = isGradientEnabled ? 'gradient' : 'solid'

  const handleModeChange = ({ backgroundMode }: { backgroundMode: string }) => {
    if (backgroundMode === 'gradient' && isGradientDisabled) {
      setWantsGradient(true)

      return
    }

    setWantsGradient(false)

    if (backgroundMode === 'gradient') {
      onChange({
        backgroundGradient: {
          ...DEFAULT_GRADIENT,
          startColor: object.backgroundColor || '@background',
          ...gradient,
          enabled: true,
        },
      })
    } else {
      onChange({
        backgroundGradient: {
          ...gradient,
          enabled: false,
        },
      })
    }
  }

  const updateGradient = (updates: Partial<BackgroundGradient>) => {
    onChange({
      backgroundGradient: {
        ...DEFAULT_GRADIENT,
        ...gradient,
        ...updates,
      },
    })
  }

  return (
    <div className="gradient-control-wrapper">
      <GenericInspectRow className="background-color-control">
        <MenuControl
          name="backgroundMode"
          options={backgroundModeOptions}
          value={currentMode}
          onChange={handleModeChange}
          displayName="Background"
        />
      </GenericInspectRow>

      {showGradientHint && appId && (
        <p className="gradient-hint-text">
          Background gradient colors are supported only in the newer version of
          React Native. To use this feature, please update to the latest version
          in{' '}
          <button
            type="button"
            className="gradient-hint-link"
            onClick={() => {
              dispatch(resetSelection())
              dispatch(openAccordion('settings', 'General'))
              dispatch(
                openAccordion('reactNativeVersion', 'reactNativeVersion')
              )
              history.push(`/apps/${appId}/app-settings?active=general`)
              setTimeout(() => {
                document
                  .getElementById('trouble-with-react-native')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }, 300)
            }}
          >
            Settings
          </button>
          .
        </p>
      )}

      {!showGradientHint && !isGradientEnabled && (
        <GenericInspectRow className="background-color-control">
          <ColorControl
            value={object.backgroundColor}
            name="backgroundColor"
            onChange={onChange}
            label="Background Color"
          />
        </GenericInspectRow>
      )}

      {!showGradientHint && isGradientEnabled && (
        <>
          <GenericInspectRow className="background-color-control">
            <MenuControl
              name="gradientType"
              options={gradientTypeOptions}
              value={gradient?.type ?? 'linear'}
              onChange={({ gradientType }: { gradientType: string }) =>
                updateGradient({ type: gradientType as 'linear' | 'radial' })
              }
              displayName="Type"
            />
          </GenericInspectRow>

          <GenericInspectRow className="background-color-control">
            <div className="gradient-color-stops">
              <ColorControl
                value={gradient?.startColor ?? '@background'}
                name="gradientStartColor"
                onChange={({
                  gradientStartColor,
                }: {
                  gradientStartColor: string
                }) => updateGradient({ startColor: gradientStartColor })}
                label="Start Color"
              />
              <ColorControl
                value={gradient?.endColor ?? '@primary'}
                name="gradientEndColor"
                onChange={({
                  gradientEndColor,
                }: {
                  gradientEndColor: string
                }) => updateGradient({ endColor: gradientEndColor })}
                label="End Color"
              />
            </div>
          </GenericInspectRow>

          {gradient?.type !== 'radial' && (
            <div className="gradient-angle-row">
              <span className="gradient-angle-label">Angle</span>
              <Slider
                name="gradientAngle"
                value={gradient?.angle ?? 180}
                min={0}
                max={360}
                onChange={({ gradientAngle }: { gradientAngle: number }) =>
                  updateGradient({ angle: gradientAngle })
                }
              />
              <div className="gradient-angle-input">
                <TextControl
                  type="number"
                  name="gradientAngle"
                  value={gradient?.angle ?? 180}
                  onChange={({ gradientAngle }: { gradientAngle: number }) =>
                    updateGradient({ angle: gradientAngle })
                  }
                  disableIncrementer
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
